import React, { useState } from 'react';
import { Row, Col, Card, Table, Button, Badge, Alert } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaPrint } from 'react-icons/fa';
import { useReactToPrint } from 'react-to-print';
import { groupeService, agentService } from '../services/api';
import GroupeModal from '../components/GroupeModal';
import ListeAgentsParGroupeImpression from '../components/ListeAgentsParGroupeImpression';

const Groupes = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [selectedGroupeForPrint, setSelectedGroupeForPrint] = useState(null);

  const queryClient = useQueryClient();
  const printRef = React.useRef();

  // Queries
  const { data: groupes = [], isLoading, error } = useQuery('groupes', () => 
    groupeService.getAll().then(res => res.data)
  );

  const { data: agents = [] } = useQuery('agents', () => 
    agentService.getAll().then(res => res.data)
  );

  // Mutations
  const deleteMutation = useMutation(groupeService.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('groupes');
      queryClient.invalidateQueries('agents'); // Pour mettre à jour les agents
    }
  });

  const handleEdit = (groupe) => {
    setSelectedGroupe(groupe);
    setShowModal(true);
  };

  const handleDelete = async (groupe) => {
    if (groupe.nombreAgents > 0) {
      alert('Impossible de supprimer ce groupe: il contient des agents. Veuillez d\'abord déplacer ou supprimer les agents de ce groupe.');
      return;
    }
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le groupe "${groupe.nom}" ?`)) {
      try {
        await deleteMutation.mutateAsync(groupe.id);
      } catch (error) {
        alert('Erreur lors de la suppression: ' + (error.response?.data || error.message));
      }
    }
  };

  const handleAddNew = () => {
    setSelectedGroupe(null);
    setShowModal(true);
  };

  // Fonction d'impression
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: selectedGroupeForPrint 
      ? `Liste_Agents_${groupes.find(g => g.id === selectedGroupeForPrint)?.nom || 'Groupe'}_${new Date().toISOString().split('T')[0]}`
      : `Liste_Agents_Par_Groupe_${new Date().toISOString().split('T')[0]}`,
    onBeforePrint: () => {
      console.log('Préparation de l\'impression...');
    },
    onAfterPrint: () => {
      console.log('Impression terminée');
      setShowPrintPreview(false);
      setSelectedGroupeForPrint(null);
    },
    removeAfterPrint: true,
  });

  const handleShowPrintPreview = (groupeId = null) => {
    setSelectedGroupeForPrint(groupeId);
    setShowPrintPreview(true);
  };

  const handleClosePrintPreview = () => {
    setShowPrintPreview(false);
    setSelectedGroupeForPrint(null);
  };

  const handlePrintGroupe = (groupeId) => {
    handleShowPrintPreview(groupeId);
  };

  const getCouleurStyle = (couleurHex) => {
    return {
      backgroundColor: couleurHex || '#6c757d',
      color: 'white',
      border: 'none'
    };
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Gestion des Groupes</h2>
          <p className="text-muted">Organisez vos agents en groupes pour faciliter la gestion</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-info" onClick={handleShowPrintPreview}>
            <FaPrint className="me-2" />
            Imprimer Liste
          </Button>
          <Button variant="primary" onClick={handleAddNew}>
            <FaPlus className="me-2" />
            Nouveau Groupe
          </Button>
        </div>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <Alert variant="danger">
          Erreur lors du chargement des groupes: {error.message}
        </Alert>
      )}

      {/* Statistiques */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="h-100 border-primary">
            <Card.Body className="text-center">
              <FaUsers size={32} className="text-primary mb-2" />
              <h4 className="text-primary">{groupes.length}</h4>
              <p className="mb-0">Groupes configurés</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-success">
            <Card.Body className="text-center">
              <FaUsers size={32} className="text-success mb-2" />
              <h4 className="text-success">
                {groupes.reduce((total, groupe) => total + (groupe.nombreAgents || 0), 0)}
              </h4>
              <p className="mb-0">Total agents assignés</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-info">
            <Card.Body className="text-center">
              <FaUsers size={32} className="text-info mb-2" />
              <h4 className="text-info">
                {groupes.filter(g => (g.nombreAgents || 0) > 0).length}
              </h4>
              <p className="mb-0">Groupes avec agents</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tableau des groupes */}
      <Card>
        <Card.Body>
          {isLoading ? (
            <div className="text-center p-4">Chargement...</div>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Nom du Groupe</th>
                  <th>Description</th>
                  <th>Couleur</th>
                  <th>Nombre d'Agents</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupes.map(groupe => (
                  <tr key={groupe.id}>
                    <td>
                      <strong>{groupe.nom}</strong>
                    </td>
                    <td>{groupe.description || 'Aucune description'}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            backgroundColor: groupe.couleurHex || '#6c757d',
                            border: '1px solid #ddd'
                          }}
                        ></div>
                        <small className="text-muted">{groupe.couleurHex || '#6c757d'}</small>
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">
                        {groupe.nombreAgents || 0} agent{(groupe.nombreAgents || 0) !== 1 ? 's' : ''}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => handlePrintGroupe(groupe.id)}
                          title="Imprimer les agents de ce groupe"
                          disabled={(groupe.nombreAgents || 0) === 0}
                        >
                          <FaPrint />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(groupe)}
                          title="Modifier"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(groupe)}
                          title="Supprimer"
                          disabled={(groupe.nombreAgents || 0) > 0}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {groupes.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center p-4">
                      <div>
                        <FaUsers size={48} className="text-muted mb-3" />
                        <h5>Aucun groupe configuré</h5>
                        <p className="text-muted">
                          Commencez par créer votre premier groupe pour organiser vos agents.
                        </p>
                        <Button variant="primary" onClick={handleAddNew}>
                          <FaPlus className="me-2" />
                          Créer le premier groupe
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Conseils d'utilisation */}
      {groupes.length > 0 && (
        <Card className="mt-4 bg-light">
          <Card.Body>
            <h6>💡 Conseils d'utilisation des groupes :</h6>
            <ul className="mb-0">
              <li>Organisez vos agents par équipe, département ou fonction</li>
              <li>Utilisez des couleurs distinctes pour chaque groupe</li>
              <li>Les groupes facilitent la génération de cartes de pointage par équipe</li>
              <li>Vous ne pouvez pas supprimer un groupe qui contient des agents</li>
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Modal */}
      <GroupeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        groupe={selectedGroupe}
      />

      {/* Modal de prévisualisation d'impression */}
      {showPrintPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            backgroundColor: '#333',
            color: 'white',
            padding: '15px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h5 style={{ margin: 0 }}>
              <FaPrint className="me-2" />
              {selectedGroupeForPrint 
                ? `Aperçu d'impression - ${groupes.find(g => g.id === selectedGroupeForPrint)?.nom || 'Groupe'}`
                : 'Aperçu d\'impression - Liste des Agents par Groupe'
              }
            </h5>
            <div>
              <Button 
                variant="primary" 
                onClick={handlePrint}
                className="me-2"
              >
                <FaPrint className="me-2" />
                Imprimer
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleClosePrintPreview}
              >
                Fermer
              </Button>
            </div>
          </div>
          <div style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'white',
            padding: '20px'
          }}>
            <div ref={printRef}>
              <ListeAgentsParGroupeImpression 
                groupes={groupes}
                agents={agents}
                selectedGroupeId={selectedGroupeForPrint}
                isPreview={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groupes; 